import { useRef } from 'react';

import { Alert } from 'cpool-components/general/Alert';
import { AlertState, AlertVariant } from 'cpool-components/general/Alert/types';
import { Button } from 'cpool-components/input/Button';
import { ButtonVariant } from 'cpool-components/input/Button/types';
import { ASSIGNED_ADVISOR_LOOKUP_KINDS } from 'cpool-constants';
import { isAbsenceActiveAt } from 'cpool-utils';
import { useTranslation } from 'react-i18next';
import { LuCheck, LuCircleX } from 'react-icons/lu';

import DialogBox from '~/components/DialogBox';
import Spinner from '~/components/icons/Spinner';
import { useAddAssignedAdvisorMutation } from '~/features/leadDetails/hooks/useAddAssignedAdvisorMutation';
import { useAssignedAdvisor } from '~/features/leadDetails/hooks/useAssignedAdvisor';
import { dateFormat } from '~/lib/i18n';

type Props = {
    loanRequestId: string;
    onClose: () => void;
    open: boolean;
};

export const AddAdvisorServiceDialog: React.FC<Props> = ({ loanRequestId, onClose, open }) => {
    const { t } = useTranslation();
    const initialFocusRef = useRef<HTMLDivElement>(null);
    const { data: lookup, isError, isPending: isLoading } = useAssignedAdvisor(loanRequestId, open);
    const { isPending, mutateAsync } = useAddAssignedAdvisorMutation(loanRequestId);

    const advisor = lookup?.kind === ASSIGNED_ADVISOR_LOOKUP_KINDS.FOUND ? lookup.advisor : null;
    const name = advisor ? `${advisor.firstName} ${advisor.lastName}` : '';
    const activeAbsence = advisor?.absence?.find((absence) => isAbsenceActiveAt(absence, Date.now()));

    return (
        <DialogBox
            title={t('LEADS.ADVISOR_SERVICE.ADD_TITLE')}
            open={open}
            onClose={onClose}
            initialFocusRef={initialFocusRef}
            panelClassName="max-w-[512px] rounded-lg p-6"
            dataTestId="add-advisor-service-dialog"
        >
            <div className="flex flex-col gap-6">
                <div className="text-sm text-midnight">
                    {isLoading && <Spinner className="max-w-6" />}
                    {!isLoading && advisor && t('LEADS.ADVISOR_SERVICE.ADD_TEXT', { name })}
                    {isError && t('LEADS.ADVISOR_SERVICE.LOOKUP_ERROR')}
                    {!isLoading && !isError && !advisor && t('LEADS.ADVISOR_SERVICE.NO_ADVISOR')}
                </div>

                {activeAbsence && (
                    <Alert state={AlertState.WARNING} variant={AlertVariant.PRIMARY} hideIcon>
                        {t('LEADS.ADVISOR_SERVICE.ADD_ABSENT', { name, until: dateFormat(activeAbsence.absenceUntil) })}
                    </Alert>
                )}

                <div className="flex justify-end gap-2">
                    <Button
                        variant={ButtonVariant.SECONDARY}
                        iconLeft={<LuCircleX />}
                        onClick={onClose}
                        disabled={isPending}
                    >
                        {t('BUTTONS.CANCEL')}
                    </Button>
                    <Button
                        variant={ButtonVariant.PRIMARY}
                        iconLeft={<LuCheck />}
                        onClick={async () => {
                            // mutateAsync promise, not mutate callbacks: those are skipped if this dialog unmounts first
                            await mutateAsync().then(onClose, () => undefined);
                        }}
                        disabled={!advisor || isPending}
                        data-testid="add-advisor-service-confirm"
                    >
                        {t('LEADS.ADVISOR_SERVICE.ADD_CONFIRM')}
                    </Button>
                </div>
            </div>
        </DialogBox>
    );
};
